import { Banner, Spinner } from "@shopify/polaris";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { usePlan } from "../hooks/usePlan";
import { type Feature, requiredPlanLabel } from "../lib/entitlements";
import { QueryErrorBanner } from "./QueryErrorBanner";

import { t } from "../lib/i18n";

const PLANS_PATH = "/plans";
const DEFAULT_TITLE = "この機能は上位プランの特典です";
const CTA = "プランを見る";

/** アップセル文言（`PlanGate` 本文と `UpgradeNote` で共有）。 */
function upsellBody(feature: Feature): string {
  return t("この機能は{plan}プラン以上でご利用いただけます。", {
    plan: requiredPlanLabel(feature),
  });
}

/**
 * アップセル見出し。プラン名は必ず `feature` から導出する — 呼び出し側に見出しを
 * 自由文字列で渡させると `feature` の最小ティアと食い違い、不要な上位プランへ誘導
 * してしまう（実際に `reorder`=Starter に「Pro以上」と表示していた）。
 */
function upsellTitle(feature: Feature, featureName?: string): string {
  if (featureName === undefined) {
    return t(DEFAULT_TITLE);
  }
  return t("{name}は{plan}プラン以上の機能です", {
    name: t(featureName),
    plan: requiredPlanLabel(feature),
  });
}

type PlanGateProps = {
  feature: Feature;
  children: ReactNode;
  /** 機能の表示名。省略時は汎用見出し。プラン名は付けない（`feature` から導出）。 */
  featureName?: string;
};

/**
 * REPLACE-mode ゲート。セクション全体を包む。プラン不足なら子の代わりに
 * アップセルBannerを表示する（生の403を見せない）。読込中はSpinner（アップセル
 * →本UIのチラつき防止）。
 */
export function PlanGate({ feature, children, featureName }: PlanGateProps) {
  const { isLoading, isError, retry, allows } = usePlan();
  const navigate = useNavigate();

  if (isLoading) {
    return <Spinner accessibilityLabel={t("読み込み中")} size="small" />;
  }

  // 取得失敗＝プラン不明。free 扱いでアップセルを見せると「サーバ障害なのに
  // アップグレードを勧める」誤誘導になるため、エラーとして再試行を出す。
  if (isError) {
    return (
      <QueryErrorBanner title={t("プラン情報の取得に失敗しました")} onRetry={retry} />
    );
  }

  if (allows(feature)) {
    return <>{children}</>;
  }

  return (
    <Banner
      tone="info"
      title={upsellTitle(feature, featureName)}
      action={{ content: t(CTA), onAction: () => navigate(PLANS_PATH) }}
    >
      {upsellBody(feature)}
    </Banner>
  );
}

type UpgradeNoteProps = {
  feature: Feature;
  /** 汎用文言の下に足す、この呼び出し箇所固有の補足（省略可）。 */
  children?: ReactNode;
};

/**
 * INLINE アップセル注記。無効化したフィールドの隣に置く用（loading/allowed の
 * 判定はしない — 呼び出し側が表示タイミングを決める）。
 */
export function UpgradeNote({ feature, children }: UpgradeNoteProps) {
  const navigate = useNavigate();

  return (
    <Banner
      tone="info"
      action={{ content: t(CTA), onAction: () => navigate(PLANS_PATH) }}
    >
      <p>{upsellBody(feature)}</p>
      {children}
    </Banner>
  );
}
